import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { SnackService } from '@shared/services/snack.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { ReportTemplateService } from '@shared/services/report-template.service';
import { TemplateEditorConfirmComponent } from '@app/main/template-editor/components';
import { TemplateBuilderService } from '@app/main/template-editor/services/template-builder.service';
import { DialogService } from '@shared/services';
import { IReportTemplate, IResponsible } from '@shared/interfaces';
import { ModuleDirective } from '@shared/directives/module.directive';
import { ModuleService } from '@shared/services/module.service';

@Component({
    selector: 'app-template-editor',
    templateUrl: './template-editor.component.html',
    styleUrls: ['./template-editor.component.scss'],
    standalone: false
})
export class TemplateEditorComponent extends ModuleDirective implements OnInit, OnDestroy {
    subscriptions: Subscription[] = [];
    isSaving: boolean;
    @Output() onClosed = new EventEmitter<IReportTemplate>();

    constructor(
        dialogService: DialogService,
        private dialogRef: MatDialogRef<TemplateEditorComponent>,
        public templateBuilder: TemplateBuilderService,
        public templateService: ReportTemplateService,
        private snack: SnackService,
        private dialog: MatDialog,
        @Inject(MAT_DIALOG_DATA) public dialogData: { mode?: string; id?: string; organisation?: IResponsible },
        moduleService: ModuleService
    ) {
        super(moduleService);
    }

    get mode(): string {
        return this.dialogData?.mode;
    }

    get formGroup(): UntypedFormGroup {
        return this.templateBuilder.formGroup;
    }

    get sections(): UntypedFormArray {
        return this.formGroup.get('sections') as UntypedFormArray;
    }

    get activeIndex(): number {
        return this.templateBuilder.activeSection;
    }

    set activeIndex(val: number) {
        this.templateBuilder.activeSection = val;
    }

    get selectedLanguage(): string {
        return this.templateBuilder.currentEditingLang;
    }

    set selectedLanguage(val: string) {
        this.templateBuilder.currentEditingLang = val;
    }

    get item(): IReportTemplate {
        return this.formGroup.value;
    }

    async ngOnInit() {
        document.getElementById('footer').style.display = 'none';
        document.getElementById('main-container').classList.add(...['no-padding', 'full-height']);

        if (this.dialogData?.id) {
            try {
                const template = await this.templateService.getItem(this.dialogData?.id);
                this.templateBuilder.buildTemplate(template, this.dialogData?.organisation, this.mode === 'duplicate');
            } catch (e) {
                this.templateBuilder.setUpTemplate(this.dialogData?.organisation);
            }
        } else {
            this.templateBuilder.setUpTemplate(this.dialogData?.organisation);
        }
    }

    ngOnDestroy(): void {
        document.getElementById('footer').style.display = 'block';
        document.getElementById('main-container').classList.remove(...['no-padding', 'full-height']);

        this.subscriptions.forEach(e => e.unsubscribe());
        this.templateBuilder.reset();
    }

    async submit() {
        this.formGroup.markAllAsTouched();

        if (this.formGroup.invalid) {
            this.snack.openSnackBarError('Please check your inputs.');
            return;
        }

        this.isSaving = true;

        if (this.mode === 'edit') {
            const result = await this.templateService.updateItem(this.formGroup.value);
            this.snack.openSnackBarSuccess(`Template ${result.title} saved successfully.`);
            this.dialogRef.close(result);
        } else {
            this.dialog
                .open(TemplateEditorConfirmComponent, {
                    position: { top: '50px' },
                    panelClass: 'custom-dialog',
                    data: {
                        mode: this.mode,
                        item:
                            this.mode === 'duplicate'
                                ? {
                                      ...this.formGroup.value,
                                      title: null,
                                      description: null
                                  }
                                : this.formGroup.value
                    }
                })
                .beforeClosed()
                .subscribe(async e => {
                    if (e) {
                        try {
                            let result: IReportTemplate;
                            e.responsibleType = this.dialogData.organisation?.responsibleType;

                            if (this.mode === 'edit') {
                                result = await this.templateService.updateItem(e);
                                this.snack.openSnackBarSuccess(`Template ${result.title} saved successfully.`);
                            } else {
                                result = await this.templateService.createItem(e);
                                this.snack.openSnackBarSuccess(`New template ${result.title} created successfully.`);
                            }

                            this.dialogRef.close(result);
                        } catch (e) {
                            this.snack.openSnackBarError(e.error.message);
                        }
                    }
                    this.isSaving = false;
                });
        }
    }

    close() {
        this.onClosed.emit();
        this.dialogRef?.close();
    }
}
